import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton } from "@mui/material";
import React from "react";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { ORDER_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useOrderData from "@/hooks/data/use-orders-data";
import useCancelOrder from "@/hooks/requests/orders/use-cancel-orders";
import { preloadOrderPdf } from "@/hooks/requests/orders/use-get-order-pdf";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrdersMessages } from "@/messages/orders/order.messages";

import { normalizeCancelOrderData } from "../../constants";
import { OrderPopover } from "../OrderPopover";

interface Props {
  order: IOrder;
  index: number;
  messages: OrdersMessages;
}

export function OrderTableRow({ order, index, messages }: Props) {
  const data = useOrderData({ order, messages });
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IOrder>();
  const { cancel } = useCancelOrder();
  const { mutate } = useMutate();
  const hasNoPermissionToPrint = useHasNoPermission(ORDER_PERMISSIONS.PRINT);

  const cancelDialog = useDialog<IOrder>();
  const { isVisible, handleOpen, handleClose, selected: selectedOrderToCancel } = cancelDialog;

  const onSuccess = () => {
    mutate(SWR_KEYS.getOrders);
    handleClose();
  };

  const onConfirmCancelOrder = async (order: IOrder) => {
    const successMessage = messages.cancelOrderSuccess;

    const formData = normalizeCancelOrderData(order);
    const result = await cancel({ formData, onSuccess, successMessage });

    if (result?.refundFormUrl) {
      window.open(result.refundFormUrl, "_blank");
    }
  };

  return (
    <>
      <TableRow index={index}>
        <TableCell>{data.orderNumber}</TableCell>
        <TableCell>{data.formattedOrderDate}</TableCell>
        <TableCell>{data.billingName}</TableCell>
        <TableCell>{data.formattedTotalAmount}</TableCell>
        <TableCell>{data.salesChannel}</TableCell>
        <TableCell>{data.formattedOrderStatus}</TableCell>

        <TableCell>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              openPopover(e, order);
              if (!hasNoPermissionToPrint) preloadOrderPdf(order.id);
            }}
          >
            <MoreHorizIcon />
          </IconButton>

          {selected && (
            <OrderPopover
              isOpen={isOpen}
              anchorEl={anchorEl}
              onClose={closePopover}
              messages={messages}
              selected={selected}
              onCancel={handleOpen}
            />
          )}

          {selectedOrderToCancel && (
            <ConfirmationDialog
              isOpen={isVisible}
              onClose={handleClose}
              onConfirm={() => onConfirmCancelOrder(selectedOrderToCancel)}
              titleText={messages.cancelOrderDialogTitle}
              descriptionText={messages.cancelOrderDialogContent}
              cancelText={messages.cancelButton}
              confirmText={messages.confirmButton}
            />
          )}
        </TableCell>
      </TableRow>
    </>
  );
}
